<!-- eslint-disable vue/no-restricted-html-elements -->
<template>
  <div class="colour-selector-drawer container">
    <div class="colour-selector__grid">
      <a
        v-for="swatch of swatches"
        :key="swatch.id"
        class="colour-selector__swatch"
        :class="[
          swatchClass(swatch),
          {
            [$class('active')]: swatch.id === product.id,
          }
        ]"
        :href="swatch.url"
      >
        <div v-if="isBackInStock(swatch)">
          <icon-bell />
        </div>

        <div class="colour-selector__swatch-image">
          <img
            v-if="swatch.image"
            :src="swatch.image"
            :alt="swatch.title"
            sizes="120px"
          >
        </div>

        <p
          class="
            colour-selector__swatch-title
            text-body-xxs-regular
            text-body-xs-regular-desktop
          "
          v-text="swatch.title"
        />

        <div
          v-if="isSoldOut(swatch)"
          class="colour-selector__swatch-sold-out-text text-other-label-xs"
          v-text="$string('product.form.colour_selector_drawer_sold_out')"
        />
        <div
          v-else-if="isBackInStock(swatch) && restockDate(swatch).length"
          class="colour-selector__swatch-back-in-stock-text text-other-label-xs"
          v-text="
            $string('product.form.restock_text', {
              replace: { date: restockDate(swatch) },
            })
          "
        />
      </a>
    </div>
  </div>
  <div class="colour-selector__footer" />
</template>

<script>

/**
 * Vue: Colour selector drawer (colour-selector-drawer)
 * -----------------------------------------------------------------------------
 * Drawer containing product color swatches.
 *
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Array} swatches - Array of objects containing swatches to be rendered
 * @param {Object} backInStock - Variant back in stock message.
 * in drawer
 *
 */
import iconBell from '~icons/general/bell.svg'

export default {
  name: 'ColourSelectorDrawer',

  components: {
    IconBell: iconBell,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    swatches: {
      type: Array,
      default: () => [],
    },
    backInStock: {
      type: Object,
      default: () => ({
        enabled: true,
      }),
    },
  },

  computed: {

    /**
     * Compute return variant selected size.
     */
    currentVariantSize() {
      const sizeOption = this.variant?.selectedOptions?.find((option) => {
        return option?.name === 'Size'
      })

      return sizeOption?.value
    },
  },

  methods: {

    /**
     * Swatch class.
     * @returns {string}
     */
    swatchClass(swatch) {
      const showBackInStock = this.showBackInStock(swatch)

      if ((!this.variant || !showBackInStock) && !swatch.available) {
        return 'colour-selector__swatch--sold-out'
      }

      const matchingVariant = swatch.variants?.find((variant) => {
        const sizeOption = variant?.selectedOptions?.find((option) => {
          return option?.name === 'Size'
        })

        if (!sizeOption) {
          return false
        }

        return sizeOption?.value === this.currentVariantSize
      })

      if (!matchingVariant) {
        return swatch.available ? '' : 'colour-selector__swatch--sold-out'
      }

      if (matchingVariant && !showBackInStock) {
        return matchingVariant?.available ? '' : 'colour-selector__swatch--sold-out'
      }

      return matchingVariant?.available || !showBackInStock ? '' : 'colour-selector__swatch--back-in-stock'
    },

    /**
     * Show back in stock.
     * @returns {Boolean}
     */
    showBackInStock(swatch) {
      const productTags = swatch.tags

      if (
        !this.backInStock.enabled ||
        productTags.includes('LTD_OLD') ||
        productTags.includes('disable back in stock') ||
        productTags.includes('injection')
      ) {
        return false
      }

      return true
    },

    /**
     * Show is sold out.
     * @returns {Boolean}
     */
    isSoldOut(swatch) {
      return this.swatchClass(swatch) === 'colour-selector__swatch--sold-out'
    },

    /**
     * Show is back in stock.
     * @returns {Boolean}
     */
    isBackInStock(swatch) {
      return this.swatchClass(swatch) === 'colour-selector__swatch--back-in-stock'
    },

    /**
     * Restock date.
     * @returns {Boolean|string}
     */
    restockDate(swatch) {
      const showBackInStock = this.showBackInStock(swatch)

      if ((!this.variant || !showBackInStock) && !swatch.available) {
        return false
      }

      const matchingVariant = swatch.variants?.find((variant) => {
        const sizeOption = variant?.selectedOptions?.find((option) => {
          return option?.name === 'Size'
        })

        if (!sizeOption) {
          return false
        }

        return sizeOption?.value === this.currentVariantSize
      })

      if (!matchingVariant) {
        return false
      }

      if (!matchingVariant?.restockDate) {
        return ''
      }

      return matchingVariant?.available || !showBackInStock ? '' : matchingVariant?.restockDate
    },
  },
}
</script>

<style lang="scss">
@import './colour-selector-drawer';
</style>
